import { call } from '@advanza/api'
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
    TrustCheckCountry,
    TrustCheckReportResponse,
    TrustCheckResultItem,
    TrustCheckRun,
    TrustCheckRunsListResponse,
    TrustCheckRunStartResponse,
    TrustCheckSummary,
    TrustCheckSummaryResponse,
} from '@trustoo/types/api/controllers/trustCheckReport'
import { FilterValue } from 'components/reports/common/ReportFilter'

const REPORT_URL = 'office/trust-check-report'
const RUNS_URL = 'office/trust-check-runs'

export interface Filters {
    run_id?: number | string
    country?: string
    outcome?: string
    check_type?: string
    check_status?: string
    sub_signal?: string
    current_status?: string
    bad_actor?: number | string
    search?: string
    [key: string]: FilterValue
}

export interface Sorting {
    field: string | null
    direction: 'ASC' | 'DESC' | null
}

export interface Pagination {
    page: number
    limit: number
    total: number
    pages: number
}

export interface TrustCheckReportState {
    runs: TrustCheckRun[]
    runsLoading: boolean
    isStarting: boolean
    startError: string | null
    /** The run the current results belong to (the latest completed one when none is selected). */
    run: TrustCheckRun | null
    pages: Record<number, string[]>
    rows: Record<string, TrustCheckResultItem>
    pagination: Pagination | null
    filters: Filters
    sorting: Sorting
    isLoading: boolean
    error: string | null
    summary: TrustCheckSummary | null
    summaryLoading: boolean
}

const errorMessage = (error: unknown, fallback: string): string => {
    if (error instanceof Error) {
        return error.message
    }
    if (error && typeof error === 'object' && 'error' in error) {
        return String((error as { error: unknown }).error)
    }
    return fallback
}

export const fetchTrustCheckRuns = createAsyncThunk(
    'trustCheckReport/fetchRuns',
    async (_: void, { rejectWithValue }) => {
        try {
            const response: TrustCheckRunsListResponse = await call(`${RUNS_URL}/get-list`)
            return response.items
        } catch (error) {
            return rejectWithValue(errorMessage(error, 'Failed to load trust check runs'))
        }
    },
)

export const startTrustCheckRun = createAsyncThunk(
    'trustCheckReport/startRun',
    async (params: { country: TrustCheckCountry | ''; months: number }, { rejectWithValue }) => {
        try {
            const response: TrustCheckRunStartResponse = await call(`${RUNS_URL}/start`, {
                method: 'POST',
                json: { country: params.country, months: params.months },
            })
            return response.item
        } catch (error) {
            return rejectWithValue(errorMessage(error, 'Failed to start a trust check run'))
        }
    },
)

export const fetchTrustCheckReport = createAsyncThunk(
    'trustCheckReport/fetchData',
    async (
        params: { filters: Filters; page?: number; limit?: number; sorting?: Sorting },
        { rejectWithValue },
    ) => {
        try {
            const { filters, page = 0, limit = 50, sorting } = params

            const response: TrustCheckReportResponse = await call(REPORT_URL, {
                method: 'GET',
                query: {
                    ...filters,
                    page,
                    limit,
                    ...(sorting?.field && { sort_field: sorting.field }),
                    ...(sorting?.direction && { sort_direction: sorting.direction }),
                },
            })

            const pages: Record<number, string[]> = {}
            const rows: Record<string, TrustCheckResultItem> = {}
            pages[page] = response.items.map((item) => String(item.trust_check_result_id))
            response.items.forEach((item) => {
                rows[String(item.trust_check_result_id)] = item
            })

            return {
                run: response.run,
                pages,
                rows,
                pagination: {
                    page,
                    limit,
                    total: response.count,
                    pages: Math.ceil(response.count / limit),
                } as Pagination,
            }
        } catch (error) {
            return rejectWithValue(errorMessage(error, 'Failed to fetch the trust check report'))
        }
    },
)

export const fetchTrustCheckSummary = createAsyncThunk(
    'trustCheckReport/fetchSummary',
    async (params: { runId?: number | string; country?: string }, { rejectWithValue }) => {
        try {
            const response: TrustCheckSummaryResponse = await call(`${REPORT_URL}/summary`, {
                method: 'GET',
                query: {
                    ...(params.runId && { run_id: params.runId }),
                    ...(params.country && { country: params.country }),
                },
            })
            return response.summary
        } catch (error) {
            return rejectWithValue(errorMessage(error, 'Failed to fetch the trust check summary'))
        }
    },
)

const initialState: TrustCheckReportState = {
    runs: [],
    runsLoading: false,
    isStarting: false,
    startError: null,
    run: null,
    pages: {},
    rows: {},
    pagination: null,
    filters: {},
    sorting: { field: null, direction: null },
    isLoading: false,
    error: null,
    summary: null,
    summaryLoading: false,
}

const trustCheckReportSlice = createSlice({
    name: 'trustCheckReport',
    initialState,
    reducers: {
        filtersChanged: (state, action: PayloadAction<Filters>) => {
            state.filters = action.payload
        },
        setSorting: (state, action: PayloadAction<Sorting>) => {
            state.sorting = action.payload
        },
        resetState: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchTrustCheckRuns.pending, (state) => {
                state.runsLoading = true
            })
            .addCase(fetchTrustCheckRuns.fulfilled, (state, action) => {
                state.runsLoading = false
                state.runs = action.payload
            })
            .addCase(fetchTrustCheckRuns.rejected, (state) => {
                state.runsLoading = false
            })
            .addCase(startTrustCheckRun.pending, (state) => {
                state.isStarting = true
                state.startError = null
            })
            .addCase(startTrustCheckRun.fulfilled, (state, action) => {
                state.isStarting = false
                state.runs = [action.payload, ...state.runs]
            })
            .addCase(startTrustCheckRun.rejected, (state, action) => {
                state.isStarting = false
                state.startError = action.payload as string
            })
            .addCase(fetchTrustCheckReport.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(fetchTrustCheckReport.fulfilled, (state, action) => {
                state.isLoading = false
                state.run = action.payload.run
                state.pages = action.payload.pages
                state.rows = action.payload.rows
                state.pagination = action.payload.pagination
                state.filters = action.meta.arg.filters
                state.error = null
            })
            .addCase(fetchTrustCheckReport.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
            })
            .addCase(fetchTrustCheckSummary.pending, (state) => {
                state.summaryLoading = true
            })
            .addCase(fetchTrustCheckSummary.fulfilled, (state, action) => {
                state.summaryLoading = false
                state.summary = action.payload
            })
            .addCase(fetchTrustCheckSummary.rejected, (state) => {
                state.summaryLoading = false
            })
    },
})

export const { filtersChanged, setSorting, resetState } = trustCheckReportSlice.actions

export default trustCheckReportSlice.reducer
